import React, { useEffect, useState } from "react";
import { ArrowLeft, Bell, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const DEFAULTS = { email_matches: true, email_messages: true };

export default function EmailNotifications() {
  const { lang } = useI18n();
  const [preferences, setPreferences] = useState(DEFAULTS);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/notifications/preferences")
      .then(({ data }) => setPreferences({ ...DEFAULTS, ...(data.preferences || {}) }))
      .catch((err) => setError(extractError(err, lang === "it" ? "Impossibile caricare le preferenze." : "Unable to load preferences.")));
  }, [lang]);

  const update = async (key) => {
    const next = !preferences[key];
    setSaving(key);
    setError("");
    try {
      const { data } = await api.patch("/notifications/preferences", { [key]: next });
      setPreferences({ ...DEFAULTS, ...(data.preferences || {}), [key]: next });
    } catch (err) {
      setError(extractError(err, lang === "it" ? "Non è stato possibile salvare la modifica." : "We could not save the change."));
    } finally {
      setSaving("");
    }
  };

  const rows = [
    {
      key: "email_matches",
      title: lang === "it" ? "Nuovi match" : "New matches",
      text: lang === "it" ? "Ricevi un’email quando nasce un nuovo match." : "Receive an email when a new match is created.",
    },
    {
      key: "email_messages",
      title: lang === "it" ? "Messaggi non letti" : "Unread messages",
      text: lang === "it"
        ? "Ricevi un’email al primo messaggio non letto di una conversazione, senza ulteriori promemoria finché non la apri."
        : "Receive one email for the first unread message in a conversation, with no further reminders until you open it.",
    },
  ];

  return (
    <div className="max-w-3xl" data-testid="email-notification-settings-page">
      <Link to="/app/settings" className="text-xs underline flex items-center gap-1" data-testid="email-settings-back">
        <ArrowLeft size={14} /> {lang === "it" ? "Impostazioni" : "Settings"}
      </Link>
      <p className="nvl-overline mt-8" style={{ color: "hsl(var(--nvl-accent))" }}>
        <Bell size={12} className="inline mr-1 -mt-0.5" /> {lang === "it" ? "Notifiche" : "Notifications"}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{lang === "it" ? "Email" : "Email"}</h1>
      <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {lang === "it"
          ? "Le notifiche email sono attive inizialmente. Puoi modificarle in qualsiasi momento."
          : "Email notifications are enabled initially. You can change them at any time."}
      </p>

      <div className="mt-8 nvl-card divide-y" style={{ borderColor: "hsl(var(--nvl-border))" }}>
        {rows.map((row) => (
          <label key={row.key} className="flex items-center gap-4 p-5 cursor-pointer">
            <Mail size={18} style={{ color: "hsl(var(--nvl-accent))" }} />
            <span className="flex-1">
              <span className="block text-sm font-medium">{row.title}</span>
              <span className="block mt-1 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{row.text}</span>
            </span>
            <input
              type="checkbox"
              checked={Boolean(preferences[row.key])}
              disabled={saving === row.key}
              onChange={() => update(row.key)}
              className="h-5 w-5"
              data-testid={`email-preference-${row.key}`}
            />
          </label>
        ))}
      </div>
      {error ? <p className="mt-4 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p> : null}
    </div>
  );
}
